/* Mesmas agregações de app/whitelabel/services.py, sobre os lançamentos do modo demonstração. */

import { MATERIAIS } from './catalogo';
import type { LinhaDemo } from './dados';
import type {
  CategoriaGhg, ColunaGhg, LinhaGas, Mes, PorCategoria, PorCategoriaGeral, PorDescricao,
  PorFornecedor, PorMaterial, PorObra, RelatorioGhg, Resumo,
} from '../tipos/api';

interface Grupo {
  k: string;
  total: number;
  n: number;
  e: Record<number, number>;
  l: LinhaDemo;
}

function agrupar(linhas: LinhaDemo[], chave: (l: LinhaDemo) => string): Grupo[] {
  const m = new Map<string, Grupo>();
  linhas.forEach((l) => {
    const k = chave(l);
    const g = m.get(k) || { k, total: 0, n: 0, e: { 1: 0, 2: 0, 3: 0 }, l };
    g.total += l.total;
    g.n++;
    if (l.escopo) g.e[l.escopo] += l.total;
    m.set(k, g);
  });
  return [...m.values()].sort((a, b) => b.total - a.total);
}

export function resumo(ls: LinhaDemo[], ano: number): Resumo {
  const soma = (f: (l: LinhaDemo) => boolean) => ls.filter(f).reduce((a, l) => a + l.total, 0);
  const e1 = soma((l) => l.escopo === 1), e2 = soma((l) => l.escopo === 2), e3 = soma((l) => l.escopo === 3);
  const loc = soma((l) => l.id_categoria === 14);
  return {
    ano, escopo_1: e1, escopo_2_total: e2, escopo_2_sem_localizacao: e2 - loc, escopo_3: e3,
    emissao_total: e1 + e2 + e3, emissao_total_sem_localizacao: e1 + e2 - loc + e3, total_linhas: ls.length,
  };
}

export function mensal(ls: LinhaDemo[], acumulado = false): Mes[] {
  const meses: Mes[] = Array.from({ length: 12 }, (_, i) => ({ mes: i + 1, escopo_1: 0, escopo_2: 0, escopo_3: 0, total_emissoes: 0 }));
  ls.forEach((l) => {
    if (!l.escopo) return;
    const m = meses[+l.data.slice(5, 7) - 1];
    m[`escopo_${l.escopo}` as 'escopo_1'] += l.total;
    m.total_emissoes += l.total;
  });
  if (acumulado) {
    const s = { escopo_1: 0, escopo_2: 0, escopo_3: 0, total_emissoes: 0 };
    meses.forEach((m) => (Object.keys(s) as (keyof typeof s)[]).forEach((k) => { s[k] += m[k]; m[k] = s[k]; }));
  }
  return meses;
}

export const porCategoria = (ls: LinhaDemo[]): PorCategoria[] =>
  agrupar(ls, (l) => l.categoria).map((g) => ({ categoria_de_emissoes: g.k, escopo: g.l.escopo || 0, total_emissoes: g.total, quantidade_registros: g.n }));

export const porEscopo = (ls: LinhaDemo[], e: number): PorCategoriaGeral[] =>
  agrupar(ls.filter((l) => l.escopo === e), (l) => l.categoria_geral)
    .map((g) => ({ escopo: e, categoria_geral_emissoes: g.k, total_emissoes: g.total, quantidade_registros: g.n }));

export const porObra = (ls: LinhaDemo[]): PorObra[] =>
  agrupar(ls.filter((l) => l.escopo), (l) => l.obra).map((g) => ({
    id_unidade: g.l.id_unidade, unidade_negocio: g.k, escopo_1: g.e[1], escopo_2: g.e[2], escopo_3: g.e[3], total_emissoes: g.total,
  }));

export const porFornecedor = (ls: LinhaDemo[]): PorFornecedor[] =>
  agrupar(ls.filter((l) => l.cnpj_fornecedor), (l) => l.cnpj_fornecedor).slice(0, 5)
    .map((g) => ({ nome_fornecedor: g.k, cnpj_fornecedor: g.k, total_emissoes: g.total, quantidade_registros: g.n }));

export const porDescricao = (ls: LinhaDemo[]): PorDescricao[] =>
  agrupar(ls, (l) => l.descricao || 'Sem descrição').slice(0, 15)
    .map((g) => ({ descricao: g.k, total_emissoes: g.total, quantidade_registros: g.n }));

export function porMaterial(ls: LinhaDemo[]): PorMaterial[] {
  const g = new Map<number, PorMaterial>();
  ls.filter((l) => l.id_categoria === 38 && l.id_material).forEach((l) => {
    const id = l.id_material as number;
    const m = MATERIAIS.find((x) => x.id === id) || { nome: 'Material', unidade: '' };
    const o = g.get(id) || { id_material: id, material: m.nome, unidade: m.unidade, quantidade: 0, valor_total: 0, total_emissoes: 0, quantidade_registros: 0 };
    o.quantidade = (o.quantidade ?? 0) + (l.quantidade || 0);
    o.valor_total += l.valor_total || 0;
    o.total_emissoes += l.total;
    o.quantidade_registros++;
    g.set(id, o);
  });
  return [...g.values()].sort((a, b) => b.total_emissoes - a.total_emissoes).slice(0, 20);
}

const GASES = ['CO2', 'CH4', 'N2O', 'HFC', 'PFC', 'SF6', 'NF3'] as const;
const COLUNAS: ColunaGhg[] = ['escopo_1', 'escopo_2_localizacao', 'escopo_2_escolha_compra', 'escopo_3'];
type Matriz = Record<string, Record<ColunaGhg, number>>;

export function relatorioGhg(ls: LinhaDemo[], ano: number): RelatorioGhg {
  const vazia = (): Matriz => Object.fromEntries(GASES.map((g) => [g, Object.fromEntries(COLUNAS.map((c) => [c, 0]))])) as Matriz;
  const ton = vazia(), tco2e = vazia(), bio = vazia();
  const cats: Record<number, Map<string, CategoriaGhg>> = { 0: new Map(), 1: new Map(), 2: new Map(), 3: new Map() };
  ls.forEach((l) => {
    const col: ColunaGhg | null = l.escopo === 1 ? 'escopo_1'
      : l.escopo === 2 ? (l.id_categoria === 15 ? 'escopo_2_escolha_compra' : 'escopo_2_localizacao')
      : l.escopo === 3 ? 'escopo_3' : null;
    const gases: Record<string, number> = { CO2: l.co2, CH4: l.ch4 * 28, N2O: l.n2o * 265 };
    const resto = l.total - gases.CO2 - gases.CH4 - gases.N2O;
    if (Math.abs(resto) > 1e-9) {
      if (l.categoria_geral === 'Emissões Fugitivas' && resto > 0) gases.HFC = resto;
      else gases.CO2 += resto;
    }
    if (col) {
      Object.entries(gases).forEach(([g, v]) => { tco2e[g][col] += v; });
      ton.CO2[col] += l.co2;
      ton.CH4[col] += l.ch4;
      ton.N2O[col] += l.n2o;
      bio.CO2[col] += l.biogenica;
    }
    const m = cats[l.escopo || 0];
    const c: CategoriaGhg = m.get(l.categoria) || {
      categoria_de_emissoes: l.categoria, emissoes_co2: 0, emissoes_ch4: 0, emissoes_n2o: 0, emissoes_hfc: 0, emissoes_pfc: 0,
      emissoes_sf6: 0, emissoes_nf3: 0, total_emissoes: 0, emissoes_co2_biogenico: 0, remocoes_co2_biogenico: 0,
    };
    Object.entries(gases).forEach(([g, v]) => { (c as unknown as Record<string, number>)[`emissoes_${g.toLowerCase()}`] += v; });
    c.total_emissoes += l.total;
    c.emissoes_co2_biogenico += l.biogenica;
    m.set(l.categoria, c);
  });
  const linhas = (mx: Matriz): LinhaGas[] => GASES.map((g) => ({ gee: g, ...mx[g] }));
  const total = (mx: Matriz): LinhaGas => ({
    gee: 'TOTAL', ...(Object.fromEntries(COLUNAS.map((c) => [c, GASES.reduce((a, g) => a + mx[g][c], 0)])) as Record<ColunaGhg, number>),
  });
  const porEsc = (e: number) => [...cats[e].values()].sort((a, b) => b.total_emissoes - a.total_emissoes);
  return {
    ano,
    emissoes_toneladas_metricas: linhas(ton), emissoes_tco2e: linhas(tco2e), emissoes_tco2e_total: total(tco2e),
    emissoes_co2_biogenico: linhas(bio), emissoes_co2_biogenico_total: total(bio),
    remocoes_co2_biogenico: linhas(vazia()), remocoes_co2_biogenico_total: total(vazia()),
    outros_gases_nao_quioto: [], outros_gases_nao_quioto_total: 0,
    categorias_sem_escopo: porEsc(0).map((c) => c.categoria_de_emissoes),
    escopo_1_por_categoria: porEsc(1), escopo_2_por_categoria: porEsc(2), escopo_3_por_categoria: porEsc(3), sem_escopo_por_categoria: porEsc(0),
  };
}
